import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { PropsWithChildren, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import styles from "./DragAndDrop.module.scss";

interface DragItem {
    index: number;
    id: string;
    type: string;
}

/**
 * Props for DragAndDropCard
 * @prop id and index - card id and index
 * @prop applyAction - to apply some action at the end of dragging (e.g. fetch)
 * @prop onMoveCard - method, called during card moving to set new card order
 * @prop type - unique string, must be equal for useDrag "accept" field and useDrop "type" prop
 * @prop canDrag - if "false" prevents item from dragging
 * @prop dragHandleOnly - if "true" the drag gesture is scoped to the grip-handle only
 *   (not the whole card body). Use this when the card contains selectable text or form
 *   fields, so a click-drag inside the body selects text instead of starting a reorder.
 *   The drop target and hover/reorder math stay on the whole card, and the whole card is
 *   still used as the drag-preview image.
 */

interface DragAndDropCardProps {
    id: string;
    index: number;
    applyAction: () => void;
    onMoveCard: (dragIndex: number, hoverIndex: number) => void;
    type: string;
    canDrag?: boolean;
    dragHandleOnly?: boolean;
}

export const DragAndDropCard = ({
    id,
    index,
    applyAction,
    onMoveCard,
    children,
    type,
    canDrag,
    dragHandleOnly,
}: PropsWithChildren<DragAndDropCardProps>) => {
    const ref = useRef<HTMLDivElement>(null);
    const dragHandleRef = useRef<HTMLDivElement>(null);
    const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: string | symbol | null }>({
        accept: type,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: DragItem, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY =
                (
                    clientOffset as {
                        x: number;
                        y: number;
                    }
                ).y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            onMoveCard(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type,
        item: () => {
            return { id, index };
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            applyAction();
        },
        canDrag: (monitor) => {
            return canDrag ?? true;
        },
    });

    const opacity = isDragging ? 0 : 1;

    if (dragHandleOnly) {
        // Drag is initiated from the grip handle only; the whole card remains the
        // drop target (hover/reorder math reads `ref`) and the drag-preview image.
        drop(ref);
        drag(dragHandleRef);
        preview(ref);
    } else {
        drag(drop(ref));
    }

    const gripIcon = <Icon icon={IconCollection.drag} />;
    return (
        <div
            ref={ref}
            style={{ opacity }}
            data-handler-id={handlerId}
            className={`${styles.cardWrapper} ${
                dragHandleOnly ? styles.handleOnlyWrapper : ""
            } ${canDrag === false ? styles.disabledCardWrapper : ""}`}
        >
            {dragHandleOnly ? (
                <div
                    ref={dragHandleRef}
                    className={styles.dragHandle}
                    data-testid="drag-handle"
                    aria-label="Drag to reorder"
                >
                    {gripIcon}
                </div>
            ) : (
                gripIcon
            )}
            {children}
        </div>
    );
};
